import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../dashboard_component';
import { cbtAPI } from '../../api';
import { ajaxconfig } from '../ajaxconfig';

const QuestionTransfer = () => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // State for outgoing transfers
  const [sourcePassCode, setSourcePassCode] = useState('');
  const [destinationPassCode, setDestinationPassCode] = useState('');
  const [securityPhrase, setSecurityPhrase] = useState('');
  const [showSecurityField, setShowSecurityField] = useState(false);
  
  // State for incoming transfers
  const [incomingPhrase, setIncomingPhrase] = useState('');
  const [incomingDestinationPassCode, setIncomingDestinationPassCode] = useState('');
  const [incomingTransferStatus, setIncomingTransferStatus] = useState('');
  
  // Security phrases management
  const [newPhrasePassCode, setNewPhrasePassCode] = useState('');
  const [generatedPhrase, setGeneratedPhrase] = useState('');
  const [email, setEmail] = useState('');
  
  // New enhanced state
  const [transferLogs, setTransferLogs] = useState([]);
  const [studentResults, setStudentResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [selectedResultsCBT, setSelectedResultsCBT] = useState('');
  
  // Shared state
  const [isLoading, setIsLoading] = useState(false);
  const [userPassCodes, setUserPassCodes] = useState([]);
  const [securityPhrases, setSecurityPhrases] = useState([]);
  const [isSameAccount, setIsSameAccount] = useState(false);
  const [selectedPasscodeDetails, setSelectedPasscodeDetails] = useState(null);
  const [destinationPasscodeDetails, setDestinationPasscodeDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('send'); // 'send', 'receive', 'manage', 'logs', 'results'
  
  const config = ajaxconfig();

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Fetch user's pass codes using centralized API
  const fetchCbtData = async () => {
    try {
      const response = await cbtAPI.getMyCBTs();
      if (response.success && Array.isArray(response.data)) {
        setUserPassCodes(response.data);
      } else {
        setUserPassCodes([]);
      }
    } catch (error) {
      toast.error('Failed to load passcode data');
      console.error('Fetch error:', error);
    }
  };

  const fetchPhraseData = async () => {
    try {
      const response = await cbtAPI.getSecurityPhrases('');
      if (response.success && Array.isArray(response.data.data)) {
        setSecurityPhrases(response.data.data);
      } else {
        setSecurityPhrases([]);
      }

    } catch (error) {
      toast.error('Failed to load security phrases');
      console.error('Fetch error:', error);
    }
  };

  const fetchTransferLogs = async () => {
    try {
      // Fetch from transfer data endpoint - will depend on backend implementation
      toast.info('Transfer logs feature coming soon');
      setTransferLogs([]);
    } catch (error) {
      console.error('Failed to load transfer logs:', error);
    }
  };

  const fetchStudentResultsForCBT = async (cbtPassCode) => {
    if (!cbtPassCode) return;
    
    try {
      setIsLoading(true);
      const response = await cbtAPI.getStudentResults(cbtPassCode);
      
      if (response.success && Array.isArray(response.data)) {
        setStudentResults(response.data);
      } else {
        setStudentResults([]);
      }
    } catch (error) {
      toast.error('Failed to load student results');
      console.error('Error fetching results:', error);
      setStudentResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch initial data
  useEffect(() => {
    fetchCbtData();
    fetchPhraseData();
  }, []);

  // Fetch logs when logs tab is active
  useEffect(() => {
    if (activeTab === 'logs') {
      fetchTransferLogs();
    }
  }, [activeTab]);

  // Fetch results when results tab is active and CBT is selected
  useEffect(() => {
    if (activeTab === 'results' && selectedResultsCBT) {
      fetchStudentResultsForCBT(selectedResultsCBT);
    }
  }, [activeTab, selectedResultsCBT]);

  // Update passcode details when selection changes
  useEffect(() => {
    if (sourcePassCode) {
      const details = userPassCodes.find(p => p.cbt_data_id === sourcePassCode);
      setSelectedPasscodeDetails(details);
    } else {
      setSelectedPasscodeDetails(null);
    }
  }, [sourcePassCode, userPassCodes]);

  // Update destination passcode details
  useEffect(() => {
    if (destinationPassCode) {
      const details = userPassCodes.find(p => p.cbt_data_id === destinationPassCode);
      setDestinationPasscodeDetails(details);
    } else {
      setDestinationPasscodeDetails(null);
    }
  }, [destinationPassCode, userPassCodes]);

  // Check if pass codes belong to same account
  const checkAccountOwnership = async () => {
    if (!sourcePassCode || !destinationPassCode) return;
    
    try {
      setIsLoading(true);
      const sourceOwned = userPassCodes.some(pc => pc.cbt_data_id === sourcePassCode);
      const destOwned = userPassCodes.some(pc => pc.cbt_data_id === destinationPassCode);
      
      // Both passcodes belong to current user = same account
      const isOwned = sourceOwned && destOwned;
      setIsSameAccount(isOwned);
      setShowSecurityField(!isOwned);
      
      // If destination doesn't belong to user, we need security phrase
      if (!destOwned) {
        setShowSecurityField(true);
      }
    } catch (error) {
      toast.error('Ownership check failed');
      console.error('Ownership check error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle outgoing question transfer using centralized API
  const handleOutgoingTransfer = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const payload = {
        source_pass_code: sourcePassCode,
        destination_pass_code: destinationPassCode,
        security_phrase: showSecurityField ? securityPhrase : null
      };
      
      const response = await cbtAPI.transferQuestions(payload);
      
      if (response.success) {
        toast.success(response.data?.message || 'Questions transferred successfully!');
        
        // Log the transfer
        const transferLog = {
          id: Date.now(),
          source: sourcePassCode,
          destination: destinationPassCode,
          date: new Date().toISOString(),
          status: 'success',
          question_count: response.data?.transferred_count || 0
        };
        
        setTransferLogs([...transferLogs, transferLog]);
        
        // Reset form
        setSourcePassCode('');
        setDestinationPassCode('');
        setSecurityPhrase('');
        setShowSecurityField(false);
        setSelectedPasscodeDetails(null);
        setDestinationPasscodeDetails(null);
      } else {
        throw new Error(response.error || 'Transfer failed');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Transfer failed';
      toast.error(errorMsg);
      
      // Log failed transfer
      const transferLog = {
        id: Date.now(),
        source: sourcePassCode,
        destination: destinationPassCode,
        date: new Date().toISOString(),
        status: 'failed',
        error: errorMsg
      };
      
      setTransferLogs([...transferLogs, transferLog]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle incoming question transfer using centralized API
  const handleIncomingTransfer = async (e) => {
    e.preventDefault();
    if (!incomingPhrase || !incomingDestinationPassCode) {
      toast.error('Please fill all fields');
      return;
    }
    
    setIsLoading(true);
    setIncomingTransferStatus('Verifying phrase...');
    
    try {
      const payload = {
        security_phrase: incomingPhrase,
        destination_pass_code: incomingDestinationPassCode
      };
      
      const response = await cbtAPI.transferQuestions(payload);
      
      if (response.success) {
        setIncomingTransferStatus('Transfer successful!');
        toast.success(response.data?.message || 'Questions received successfully!');
        
        // Reset form after success
        setIncomingPhrase('');
        setIncomingDestinationPassCode('');
        
        // Refresh data
        setTimeout(() => {
          setIncomingTransferStatus('');
          fetchCbtData();
        }, 3000);
      } else {
        throw new Error(response.error || 'Transfer failed');
      }
    } catch (error) {
      setIncomingTransferStatus('Transfer failed');
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to receive questions';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate new security phrase using centralized API
  const generateSecurityPhrase = async () => {
    if (!newPhrasePassCode || !email) {
      toast.error('Please select a pass code and enter recipient email');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await cbtAPI.createSecurityPhrase({ 
        pass_code: newPhrasePassCode,
        email
      });
      
      if (response.success && response.data?.phrase_key) {
        setGeneratedPhrase(response.data.phrase_key);
        
        const newPhrase = {
          id: Date.now().toString(),
          cbt_pass_code: newPhrasePassCode,
          phrase_key: response.data.phrase_key,
          email: email,
          created_at: new Date().toISOString(),
          status: 'active'
        };
        
        setSecurityPhrases([...securityPhrases, newPhrase]);
        toast.success('Security phrase generated successfully!');
        setEmail('');
        setNewPhrasePassCode('');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate security phrase';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete security phrase using centralized API
//   const deleteSecurityPhrase = async (id) => {
//   try {
//     setIsLoading(true);

//     const response = await cbtAPI.deleteSecurityPhrase(id);

//     if (response.success) {
//       setSecurityPhrases(
//         securityPhrases.filter(phrase => phrase.id !== id)
//       );
//       toast.success('Security phrase deleted');
//     } else {
//       throw new Error(response.error || 'Delete failed');
//     }
//   } catch (error) {
//     const errorMsg =
//       error.response?.data?.detail ||
//       error.message ||
//       'Delete failed';
//     toast.error(errorMsg);
//   } finally {
//     setIsLoading(false);
//   }
// };


const deleteSecurityPhrase = async (id) => {
  if (!id) {
    toast.error('Invalid security phrase ID');
    return;
  }

  try {
    setIsLoading(true);

    const response = await cbtAPI.deleteSecurityPhrase(Number(id));

    if (response.success) {
      setSecurityPhrases(prev =>
        prev.filter(phrase => phrase.id !== id)
      );
      toast.success('Security phrase deleted');
    } else {
      throw new Error(response.error || 'Delete failed');
    }
  } catch (error) {
    const errorMsg =
      error.response?.data?.detail ||
      error.message ||
      'Delete failed';
    toast.error(errorMsg);
  } finally {
    setIsLoading(false);
  }
};


  // Fetch student details using centralized API
  const fetchStudentDetails = async (studentId) => {
    try {
      setIsLoading(true);
      const response = await cbtAPI.getStudentResult(selectedResultsCBT, studentId);
      if (response.success) {
        setStudentDetails(response.data);
        setSelectedStudent(studentId);
      } else {
        toast.error('Failed to load student details');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to load student details';
      toast.error(errorMsg);
      console.error('Error fetching student details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Passcode information card
  const PasscodeInfoCard = ({ passcode, title }) => (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
      <h4 className="text-sm font-medium text-blue-800 mb-2">{title}</h4>
      {passcode ? (
        <div className="space-y-1">
          <div className="flex">
            <span className="text-gray-600 w-24">Name:</span>
            <span className="font-medium">{passcode.cbt_name}</span>
          </div>
          <div className="flex">
            <span className="text-gray-600 w-24">Status:</span>
            <span className="capitalize font-medium">
              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${passcode.cbt_status === 'published' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
              {passcode.cbt_status}
            </span>
          </div>
          <div className="flex">
            <span className="text-gray-600 w-24">Created:</span>
            <span className="font-medium">
              {new Date(passcode.created_at).toLocaleDateString()}
            </span>
          </div>
          <div className="flex">
            <span className="text-gray-600 w-24">ID:</span>
            <code className="font-mono text-sm bg-blue-100 px-1 rounded">{passcode.cbt_data_id}</code>
          </div>
        </div>
      ) : (
        <p className="text-gray-500 text-sm">No information available</p>
      )}
    </div>
  );

  // Student Results Table Component
  const StudentResultsTable = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    return (
      <div className="mt-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Student Results</h4>
        <div className="overflow-x-auto bg-white rounded-lg shadow border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {studentResults.length > 0 ? (
                studentResults.map((result, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{result.user_id || result.student_id}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{result.cbt_test_id}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        parseFloat(result.cbt_total_score) >= 70 
                          ? 'bg-green-100 text-green-800' 
                          : parseFloat(result.cbt_total_score) >= 50 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-red-100 text-red-800'
                      }`}>
                        {result.cbt_total_score}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {result.submitted_at ? new Date(result.submitted_at).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => fetchStudentDetails(result.user_id || result.student_id)}
                        className="text-blue-600 hover:text-blue-900 font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No student results found for this CBT
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Student Details Modal
  const StudentDetailsModal = () => {
    if (!studentDetails || !selectedStudent) return null;
    
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                Student Results: {selectedStudent}
              </h3>
              <button
                onClick={() => {
                  setSelectedStudent(null);
                  setStudentDetails(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-6">
            {studentDetails.final_grade && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-800 mb-2">Final Grade Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Total Score</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {studentDetails.final_grade.cbt_total_score || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Percentage</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {studentDetails.final_grade.percentage || 'N/A'}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className={`text-lg font-bold ${
                      studentDetails.final_grade.passed ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {studentDetails.final_grade.passed ? 'Passed' : 'Failed'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Time Taken</p>
                    <p className="text-lg font-bold text-gray-900">
                      {studentDetails.final_grade.time_taken ? 
                        `${Math.floor(studentDetails.final_grade.time_taken / 60)}m ${studentDetails.final_grade.time_taken % 60}s` 
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <h4 className="font-semibold text-gray-900 mb-4">Detailed Answers</h4>
            <div className="space-y-4">
              {studentDetails.answers && studentDetails.answers.length > 0 ? (
                studentDetails.answers.map((answer, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-gray-900">Question {index + 1}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        answer.is_correct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {answer.is_correct ? 'Correct' : 'Incorrect'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Answer: {answer.answer_text || answer.selected_option_id || 'No answer'}
                    </p>
                    <p className="text-sm text-gray-600">Score: {answer.score_obtained || 0}</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No answer details available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div 
        className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-grow w-full">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Question Transfer Hub</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Securely send and receive questions between pass codes with our transfer system
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="border-b border-gray-200 w-full max-w-6xl">
              <nav className="-mb-px flex flex-wrap space-x-2 md:space-x-8">
                {['send', 'receive', 'manage', 'logs', 'results'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 text-center border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab === 'send' && 'Send Questions'}
                    {tab === 'receive' && 'Receive Questions'}
                    {tab === 'manage' && 'Security Phrases'}
                    {tab === 'logs' && 'Transfer Logs'}
                    {/* {tab === 'results' && 'Student Results'} */}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="w-full">
            {/* Send Questions Tab */}
            {activeTab === 'send' && (
              <div className="bg-white rounded-lg shadow p-6 max-w-4xl mx-auto">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-indigo-100 rounded-full mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-indigo-700">Send Questions</h2>
                </div>
                
                <form onSubmit={handleOutgoingTransfer}>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      From Pass Code (Source)
                    </label>
                    <select
                      value={sourcePassCode}
                      onChange={(e) => {
                        setSourcePassCode(e.target.value);
                        // Reset destination when source changes
                        setDestinationPassCode('');
                        setShowSecurityField(false);
                        setSecurityPhrase('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    >
                      <option value="">Select Source Pass Code</option>
                      {userPassCodes.map((passCode) => (
                        <option key={passCode.cbt_data_id} value={passCode.cbt_data_id}>
                          {passCode.cbt_name} ({passCode.cbt_data_id})
                        </option>
                      ))}
                    </select>
                    
                    <PasscodeInfoCard 
                      passcode={selectedPasscodeDetails} 
                      title="Source Passcode Details" 
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      To Pass Code (Destination)
                    </label>
                    <input
                      type="text"
                      value={destinationPassCode}
                      onChange={(e) => {
                        setDestinationPassCode(e.target.value);
                        if (sourcePassCode && e.target.value) {
                          checkAccountOwnership();
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter destination pass code"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Enter the pass code you want to transfer questions to
                    </p>
                    
                    <PasscodeInfoCard 
                      passcode={destinationPasscodeDetails} 
                      title="Destination Passcode Details" 
                    />
                  </div>

                  {showSecurityField && (
                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Security Phrase
                      </label>
                      <input
                        type="text"
                        value={securityPhrase}
                        onChange={(e) => setSecurityPhrase(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Enter security phrase"
                        required={showSecurityField}
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Required for cross-account transfers. Ask the recipient for this phrase.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full flex justify-center py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                      isLoading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending Questions...
                      </span>
                    ) : (
                      'Send Questions'
                    )}
                  </button>
                </form>
                
                <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">Transfer Rules</h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <ul className="list-disc pl-5 space-y-1">
                          <li><span className="font-medium">Same Account:</span> No security phrase needed when transferring between your own pass codes</li>
                          <li><span className="font-medium">Cross-Account:</span> Security phrase required when sending to another user's pass code</li>
                          <li>Only published questions will be transferred</li>
                          <li>Transfer is one-way (questions are copied, not moved)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Receive Questions Tab */}
            {activeTab === 'receive' && (
              <div className="bg-white rounded-lg shadow p-6 max-w-4xl mx-auto">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-green-100 rounded-full mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-green-700">Receive Questions</h2>
                </div>
                
                <form onSubmit={handleIncomingTransfer}>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Security Phrase
                    </label>
                    <input
                      type="text"
                      value={incomingPhrase}
                      onChange={(e) => setIncomingPhrase(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      placeholder="Enter security phrase from sender"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Provided by the sender of the questions
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      To Pass Code (Your CBT)
                    </label>
                    <select
                      value={incomingDestinationPassCode}
                      onChange={(e) => setIncomingDestinationPassCode(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      required
                    >
                      <option value="">Select Your Pass Code</option>
                      {userPassCodes.map((passCode) => (
                        <option key={passCode.cbt_data_id} value={passCode.cbt_data_id}>
                          {passCode.cbt_name} ({passCode.cbt_data_id})
                        </option>
                      ))}
                    </select>
                    
                    <PasscodeInfoCard 
                      passcode={userPassCodes.find(p => p.cbt_data_id === incomingDestinationPassCode)} 
                      title="Your Passcode Details" 
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full flex justify-center py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                      isLoading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Receiving Questions...
                      </span>
                    ) : (
                      'Receive Questions'
                    )}
                  </button>
                  
                  {incomingTransferStatus && (
                    <div className={`mt-4 p-3 rounded-md text-center ${
                      incomingTransferStatus.includes('success') 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {incomingTransferStatus}
                    </div>
                  )}
                </form>
                
                <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">Receiving Questions</h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>To receive questions from another account:</p>
                        <ol className="list-decimal pl-5 mt-1 space-y-1">
                          <li>Provide the sender with a security phrase from the Security Phrases tab</li>
                          <li>They'll send you the phrase after initiating the transfer</li>
                          <li>Enter the phrase here and select your destination passcode</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Phrases Tab */}
            {activeTab === 'manage' && (
              <div className="bg-white rounded-lg shadow p-6 max-w-6xl mx-auto">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-purple-100 rounded-full mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-purple-700">Security Phrase Management</h2>
                </div>
                
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3">Generate New Phrase</h3>
                  
                  <div className="mb-3">
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Recipient Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                      placeholder="recipient@example.com"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Pass Code to Share
                    </label>
                    <select
                      value={newPhrasePassCode}
                      onChange={(e) => setNewPhrasePassCode(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">Select Pass Code</option>
                      {userPassCodes.map((passCode) => (
                        <option key={passCode.cbt_data_id} value={passCode.cbt_data_id}>
                          {passCode.cbt_name} ({passCode.cbt_data_id})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <button
                    onClick={generateSecurityPhrase}
                    disabled={isLoading || !newPhrasePassCode || !email}
                    className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                      isLoading || !newPhrasePassCode || !email
                        ? 'bg-gray-400'
                        : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    {isLoading ? 'Generating...' : 'Generate Security Phrase'}
                  </button>
                  
                  {generatedPhrase && (
                    <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-md">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-purple-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1">
                          <h3 className="text-sm font-medium text-purple-800">Security Phrase Created!</h3>
                          <div className="mt-2 text-sm text-purple-700">
                            <p className="mb-2">Share this phrase with the sender:</p>
                            <div className="flex items-center justify-between p-3 bg-white rounded border border-purple-200">
                              <code className="font-mono">{generatedPhrase}</code>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(generatedPhrase);
                                  toast.info('Phrase copied to clipboard');
                                }}
                                className="ml-2 px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-sm"
                              >
                                Copy
                              </button>
                            </div>
                            <p className="mt-2 text-xs">
                              This phrase allows others to transfer questions to your passcode
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-3">Your Security Phrases</h3>
                  {securityPhrases.length === 0 ? (
                    <div className="text-center py-8">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No security phrases</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Create a security phrase to allow others to transfer questions to your passcodes
                      </p>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-md max-h-[500px] overflow-y-auto overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Pass Code
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              CBT Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Recipient Email
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Phrase Key
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {securityPhrases.map((phrase) => {
                            const passcode = userPassCodes.find(
                              (p) => p.cbt_data_id === phrase.cbt_pass_code
                            );
                            return (
                              <tr key={phrase.id} className="hover:bg-gray-50">
                                <td className="px-4 py-4 text-sm font-medium text-gray-900">
                                  {phrase.cbt_pass_code}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">
                                  {passcode ? passcode.cbt_name : "N/A"}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">
                                  {phrase.email}
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">
                                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                    {phrase.phrase_key}
                                  </code>
                                </td>
                                <td className="px-4 py-4 text-sm text-gray-500">
                                  {/* <button
                                    onClick={() => deleteSecurityPhrase(phrase.id)}
                                    disabled={isLoading}
                                    className="text-red-600 hover:text-red-900 font-medium"
                                  >
                                    Revoke
                                  </button> */}

                                  <button
  onClick={() => deleteSecurityPhrase(phrase.id)}
  className="text-red-600 hover:text-red-800"
>
  Delete
</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Transfer Logs Tab */}
            {activeTab === 'logs' && (
              <div className="bg-white rounded-lg shadow p-6 max-w-6xl mx-auto">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-gray-100 rounded-full mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-700">Transfer History</h2>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transferLogs.length > 0 ? (
                        transferLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {new Date(log.date).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{log.source}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{log.destination}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{log.question_count || 'N/A'}</td>
                            <td className="px-6 py-4 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                log.status === 'success' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {log.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                            No transfer logs found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Student Results Tab */}
            {activeTab === 'results' && (
              <div className="bg-white rounded-lg shadow p-6 max-w-6xl mx-auto">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-100 rounded-full mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-blue-700">Student Results</h2>
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Select CBT to View Results
                  </label>
                  <select
                    value={selectedResultsCBT}
                    onChange={(e) => setSelectedResultsCBT(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select CBT Pass Code</option>
                    {userPassCodes.map((passCode) => (
                      <option key={passCode.cbt_data_id} value={passCode.cbt_data_id}>
                        {passCode.cbt_name} ({passCode.cbt_data_id})
                      </option>
                    ))}
                  </select>
                </div>
                
                {selectedResultsCBT && <StudentResultsTable />}
              </div>
            )}
          </div>

          <div className="mt-8 bg-blue-50 border-l-4 border-blue-400 p-4 rounded max-w-7xl mx-auto w-full">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">How Question Transfer Works</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc pl-5 space-y-1">
                    <li><span className="font-medium">Send Questions:</span> Transfer questions from your passcode to another account</li>
                    <li><span className="font-medium">Receive Questions:</span> Accept questions from other accounts using their security phrase</li>
                    <li><span className="font-medium">Security Phrase:</span> Generate phrases to authorize incoming transfers to your passcodes</li>
                    <li><span className="font-medium">Transfer Logs:</span> Track all question transfers with detailed history</li>
                    <li><span className="font-medium">Student Results:</span> View detailed performance analytics for your CBTs</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </div>
      </div>

      {/* Render Student Details Modal */}
      {selectedStudent && <StudentDetailsModal />}
    </div>
  );
};

export default QuestionTransfer;